import clsx from 'clsx'
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import {
  property,
  query,
  queryAssignedElements,
  state,
} from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { CommonFormElementSize, InputTypes } from '@/common/types'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'

import styles from './_styles/input.scss?inline'

export class InputBaseElement extends GenericFormElement {
  @query('#input') input!: HTMLInputElement
  @queryAssignedElements({ slot: 'addon-before' })
  private readonly _addonBeforeNode!: NodeListOf<HTMLElement>
  @queryAssignedElements({ slot: 'addon-after' })
  private readonly _addonAfterNode!: NodeListOf<HTMLElement>
  @queryAssignedElements({ slot: 'prefix' })
  private readonly _prefixNode!: NodeListOf<HTMLElement>
  @queryAssignedElements({ slot: 'postfix' })
  private readonly _postfixNode!: NodeListOf<HTMLElement>

  @property() type: InputTypes = 'text'
  @property() placeholder?: string
  @property({ type: Number }) minlength?: number
  @property({ type: Number }) maxlength?: number
  @property({ type: Number }) min?: number
  @property({ type: Number }) max?: number
  @property() pattern?: string
  @property({ type: Number }) step?: number | 'any' = 1
  @property() inputmode?: string
  @property({ type: Boolean, reflect: true }) loading = false
  @property({ type: Boolean }) manualValueSet = false
  @property({ type: Boolean }) disableEnter = false
  @state() private _hasAddonBefore = false
  @state() private _hasAddonAfter = false
  @state() private _hasPrefix = false
  @state() private _hasPostfix = false

  static readonly styles = unsafeCSS(styles)

  public get nativeControl(): HTMLInputElement {
    return this.shadowRoot?.querySelector('.input__control') as HTMLInputElement
  }

  private _handleSlotChange(event: Event) {
    const target = event?.target as HTMLInputElement
    target.setAttribute('slot', 'icon')
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    // this.role = 'textbox'
    this._hasAddonBefore = !!this._addonBeforeNode.length
    this._hasAddonAfter = !!this._addonAfterNode.length
    this._hasPrefix = !!this._prefixNode.length
    this._hasPostfix = !!this._postfixNode.length
  }

  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    super.attributeChangedCallback(name, oldValue, newValue)

    if (name === 'value' && this.input) {
      this.input.value = newValue
    }
  }

  onInput = () => {
    if (!this.input.validity.badInput && !this.manualValueSet) {
      this.value = this.input.value
    }

    this.emit('dds-input', { value: this.input.value })
    this.setValidity()
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.input.value = this.defaultValue || ''
  }

  onBlur() {
    this.emit('dds-blur')
  }

  onFocus() {
    this.emit('dds-focus')
  }

  onChange() {
    this.value = this.input.value

    if (!this.manualValueSet) {
      this.emit('dds-change', { value: this.input.value })
    }

    this.setValidity()
  }

  onKeydown(event: KeyboardEvent) {
    this.emit('dds-keydown', { value: this.input.value, originalEvent: event })

    const hasModifier =
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey

    if (!this.disableEnter && event.key === 'Enter' && !hasModifier) {
      setTimeout(() => {
        if (!event.defaultPrevented && !event.isComposing) {
          const proxy = document.createElement('button')
          proxy.type = 'submit'
          this.insertAdjacentElement('afterend', proxy)

          proxy.click()
          proxy.remove()
        }
      })
    }
  }

  public get focusElement() {
    return this.input
  }

  /**
   * Gets the aria-describedby attribute value by combining description and feedback IDs.
   * @returns {string | undefined} Space-separated IDs or undefined if none exist
   * @private
   */
  private getAriaDescribedBy(): string | undefined {
    const ids: string[] = []

    if (this.description) {
      ids.push('description')
    }

    if (this.feedback) {
      ids.push('feedback')
    }

    return ids.length > 0 ? ids.join(' ') : undefined
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  renderDefaultContent() {
    return html` <span
        part="prefix"
        class=${clsx({ 'input__addon-prefix': !!this._hasPrefix })}>
        <slot name="prefix"></slot>
      </span>
      <span
        part="postfix"
        class=${clsx({
          'input__addon-postfix': this._hasPostfix || this.loading,
        })}>
        ${this.loading
          ? html`<dap-ds-spinner size="lg"></dap-ds-spinner>`
          : nothing}
        <slot name="postfix"></slot>
      </span>`
  }

  renderInput(content?: unknown) {
    return html`
      <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id="label"
          for="input"
          .size=${this.effectiveSize}
          .required=${this.required}
          .optional=${this.optional}
          .disabled=${this.disabled}
          class=${clsx('label')}
          .noMargin=${!!this.description}
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          floatingStrategy="fixed"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            clean
            class="info-button"
            slot="trigger"
            aria-label=${this.tooltipAriaLabel}
            size=${this.effectiveSize as 'xs' | 'sm' | 'md' | 'lg'}
            iconSize=${this.effectiveSize === 'xs' ? '16' : '22'}>
            <dap-ds-icon
              name="information-line"
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          class=${clsx('input-description')}
          size=${ifDefined(this.effectiveSize as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `}
      <span
        part="base"
        class=${clsx(
          'input',
          `input--${this.effectiveSize}`,
          { 'input--success': this.status === 'success' },
          { 'input--error': this.status === 'error' },
          { 'input--disabled': this.disabled },
          { 'input--readonly': this.readonly },
          { 'input--has-addon-before': !!this._hasAddonBefore },
          { 'input--has-addon-after': !!this._hasAddonAfter },
          { 'input--has-prefix': !!this._hasPrefix },
          { 'input--has-postfix': !!this._hasPostfix || this.loading },
        )}>
        <span
          part="addon-before"
          class=${clsx({
            'input__addon-before': !!this._hasAddonBefore,
            [`input__addon-before--${this.effectiveSize}`]:
              !!this._hasAddonBefore,
            'input__addon-before--disabled':
              !!this._hasAddonBefore && this.disabled,
            'input__addon-before--readonly':
              !!this._hasAddonBefore && this.readonly,
            'input__addon-before--success':
              !!this._hasAddonBefore && this.status === 'success',
            'input__addon-before--error':
              !!this._hasAddonBefore && this.status === 'error',
          })}>
          <slot name="addon-before"></slot>
        </span>
        <div class="input__container">
          <input
            id="input"
            class=${clsx(
              'input__control',
              `input__control--${this.effectiveSize}`,
              { 'input__control--success': this.status === 'success' },
              { 'input__control--error': this.status === 'error' },
              { 'input__control--disabled': this.disabled },
              { 'input__control--readonly': this.readonly },
              { 'input__control--has-addon-before': !!this._hasAddonBefore },
              { 'input__control--has-addon-after': !!this._hasAddonAfter },
              { 'input__control--has-prefix': !!this._hasPrefix },
              {
                'input__control--has-postfix':
                  !!this._hasPostfix || this.loading,
              },
            )}
            part="input"
            name=${ifDefined(this.name ? this.name : undefined)}
            type=${this.type}
            ?disabled=${this.disabled}
            ?readonly=${this.readonly}
            ?required=${this.required}
            placeholder=${ifDefined(this.placeholder)}
            value=${ifDefined(this.value)}
            minlength=${ifDefined(this.minlength)}
            maxlength=${ifDefined(this.maxlength)}
            aria-labelledby=${ifDefined(this.label ? 'label' : nothing)}
            aria-describedby=${ifDefined(this.getAriaDescribedBy())}
            min=${ifDefined(this.min)}
            max=${ifDefined(this.max)}
            pattern=${ifDefined(this.pattern)}
            step=${ifDefined(this.step as number)}
            inputmode=${ifDefined(this.inputmode)}
            ?autofocus=${this.autofocus}
            autocomplete=${this.autocomplete as any}
            autocapitalize=${ifDefined(this.autocapitalize ? 'on' : 'off')}
            @change=${this.onChange}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
            @invalid=${this.handleInvalid}
            @blur=${this.onBlur}
            @focus=${this.onFocus} />
          ${content ?? this.renderDefaultContent()}
        </div>
        <span
          part="addon-after"
          class=${clsx({
            'input__addon-after': !!this._hasAddonAfter,
            [`input__addon-after--${this.effectiveSize}`]:
              !!this._hasAddonAfter,
            'input__addon-after--disabled':
              !!this._hasAddonAfter && this.disabled,
            'input__addon-after--readonly':
              !!this._hasAddonAfter && this.readonly,
            'input__addon-after--success':
              !!this._hasAddonAfter && this.status === 'success',
            'input__addon-after--error':
              !!this._hasAddonAfter && this.status === 'error',
          })}>
          <slot name="addon-after"></slot>
        </span>
      </span>
      ${this.feedback && this._renderFeedback()}
    `
  }

  private _renderFeedback() {
    return html`
      <dap-ds-feedback
        part="feedback"
        role="alert"
        feedbackType=${ifDefined(this.feedbackType)}
        feedbackSize="${ifDefined(this.effectiveSize as 'lg' | 'sm' | 'xs')}"
        .subtle=${this.subtle}
        id="feedback">
        <slot
          name="feedback-icon"
          @slotchange="${this._handleSlotChange}"></slot>
        ${this.feedback}
      </dap-ds-feedback>
    `
  }
}
